/**
 * LASH eXtend — 1+1 Free Sale (convert-in-place, manual add)
 * -------------------------------------------------------------------------
 * The customer adds the items themselves. Within a group of the SAME product
 * at the SAME price, every 2 units => 1 becomes free (1+1). Nothing is auto-
 * added: a single unit stays paid; the free only appears once the customer
 * adds the pair. Stacks, capped at 5 free per order.
 *
 * Example: add Product A / variant a (paid), then add Product A / variant a,b
 * or c -> that pairing makes one unit free (same product, same price).
 *
 * A free unit is created by CONVERTING a unit already in the cart (split its
 * paid line, re-add it as a free-tagged line) — the cart total quantity never
 * changes, so there is no "extra" product added.
 *
 * Free lines carry:
 *   _december_free_gift : "yes"  -> existing discount function zeroes it
 *   _one_plus_one       : "yes"  -> so THIS engine only manages its own lines
 *
 * Grouping key = product_id + ORIGINAL price. Using original_price (never the
 * discounted/zeroed price) keeps a freed unit in the same group as its paid
 * pair — this is what prevents the add/remove loop.
 *
 * Globals (layout/theme.liquid):
 *   window.ONE_PLUS_ONE_ENABLED  (bool)
 *   window.ONE_PLUS_ONE_IDS      (array) eligible product IDs (allow-list), OR
 *   window.ONE_PLUS_ONE_EXCLUDE  (array) course product IDs (deny-list)
 * -------------------------------------------------------------------------
 */
(function () {
  "use strict";

  const CONFIG = {
    freeProperty: "_december_free_gift",
    freeValue: "yes",
    groupProperty: "_one_plus_one",
    perPair: 2,                         // 2 units of same product+price => 1 free
    maxFreePerOrder: 5,
    enabled: window.ONE_PLUS_ONE_ENABLED === true,
    allowIds: (window.ONE_PLUS_ONE_IDS || []).map(Number),
    excludeIds: (window.ONE_PLUS_ONE_EXCLUDE || []).map(Number),
    debounceMs: 250,
  };

  const state = { running: false, rerun: false };

  // --- property helpers ---------------------------------------------------
  function propVal(line, name) {
    const p = line.properties;
    if (!p) return null;
    if (Array.isArray(p)) {
      const f = p.find((x) => x && x[0] === name);
      return f ? f[1] : null;
    }
    return p[name] != null ? p[name] : null;
  }
  const truthy = (v) => v != null && ["true", "yes", "1"].includes(String(v).toLowerCase());
  const isFreeGift = (l) => truthy(propVal(l, CONFIG.freeProperty)); // any free line
  const isOwnGift = (l) => truthy(propVal(l, CONFIG.groupProperty)); // ours only

  const isEligible = (l) => {
    const pid = Number(l.product_id);
    if (CONFIG.allowIds.length) return CONFIG.allowIds.includes(pid);
    return !CONFIG.excludeIds.includes(pid);
  };

  // Stable price (pre-discount) so a zeroed free line stays with its paid pair.
  const unitPrice = (l) => (l.original_price != null ? l.original_price : l.price);
  const groupKey = (l) => l.product_id + "@" + unitPrice(l);

  // --- cart API -----------------------------------------------------------
  async function getCart() {
    const r = await fetch("/cart.js", { headers: { Accept: "application/json" } });
    return r.json();
  }
  async function changeLine(key, quantity) {
    const r = await fetch("/cart/change.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ id: key, quantity }),
    });
    return r.json();
  }
  async function addUnit(variantId, quantity, asFree) {
    const item = { id: Number(variantId), quantity };
    if (asFree) {
      item.properties = { [CONFIG.freeProperty]: CONFIG.freeValue, [CONFIG.groupProperty]: "yes" };
    }
    const r = await fetch("/cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ items: [item] }),
    });
    return r.json();
  }

  // Decide target free count per (product_id + price) group after the cap.
  function plan(cart) {
    const lines = (cart.items || []).filter(isEligible);

    const map = new Map();
    for (const l of lines) {
      const key = groupKey(l);
      const g = map.get(key) || { key, price: unitPrice(l), paidLines: [], freeLines: [], paid: 0, currentFree: 0 };
      if (isOwnGift(l)) {
        g.freeLines.push(l);
        g.currentFree += l.quantity;
      } else if (!isFreeGift(l)) {
        g.paidLines.push(l);
        g.paid += l.quantity;
      }
      map.set(key, g);
    }

    const groups = Array.from(map.values());
    if (!CONFIG.enabled) {
      groups.forEach((g) => (g.target = 0)); // sale off -> convert all free back to paid
      return groups;
    }

    // 1+1: earned = floor(totalUnits / 2). Order cap 5, cheapest groups first.
    let remaining = CONFIG.maxFreePerOrder;
    groups
      .slice()
      .sort((a, b) => a.price - b.price)
      .forEach((g) => {
        const total = g.paid + g.currentFree;
        const earned = Math.floor(total / CONFIG.perPair);
        g.target = Math.min(earned, remaining);
        remaining -= g.target;
      });

    return groups;
  }

  async function reconcile() {
    if (state.running) { state.rerun = true; return; }
    state.running = true;
    try {
      // Use the cart the theme handed us in the event (post-change, fresh).
      // The drawer stepper updates optimistically and fires cart:updated before
      // the server reflects it, so a fresh /cart.js can be stale — that was the
      // "only fixes on the second change" bug. Fall back to fetch if no event
      // cart was captured (e.g. on load).
      let cart = pendingCart;
      pendingCart = null;
      if (!cart) cart = await getCart();

      const groups = plan(cart);
      let last = null;

      // Pass A — REMOVALS first (free -> paid). Strips the free attribute from
      // any group that no longer qualifies (e.g. the cap moved its slot to a
      // cheaper product), and frees cap budget before Pass B spends it.
      for (const g of groups) {
        let excess = g.currentFree - g.target;
        if (excess <= 0) continue;
        for (const line of g.freeLines) {
          if (excess <= 0) break;
          const take = Math.min(excess, line.quantity);
          last = await changeLine(line.key, line.quantity - take);
          last = await addUnit(line.variant_id, take, false);
          excess -= take;
        }
      }

      // Pass B — ADDITIONS (paid -> free).
      for (const g of groups) {
        let need = g.target - g.currentFree;
        if (need <= 0) continue;
        for (const line of g.paidLines) {
          if (need <= 0) break;
          const take = Math.min(need, line.quantity);
          last = await changeLine(line.key, line.quantity - take);
          last = await addUnit(line.variant_id, take, true);
          need -= take;
        }
      }

      // Re-render via the theme's convention. The resulting cart:updated carries
      // the fresh cart, which triggers one more (idempotent) reconcile that
      // verifies we've settled — cross-event convergence, no internal loop.
      if (last) {
        document.dispatchEvent(
          new CustomEvent("ajaxProduct:added", {
            detail: { product: last, addToCartBtn: null },
          })
        );
      }
    } catch (e) {
      console.error("[1+1] reconcile failed", e);
    } finally {
      state.running = false;
      if (state.rerun) { state.rerun = false; reconcile(); }
    }
  }

  let timer = null;
  let pendingCart = null; // freshest cart handed to us by a cart:updated event

  function schedule(evt) {
    // Capture the event's cart when present — it reflects the change that
    // triggered this, unlike a possibly-stale /cart.js fetch.
    const c = evt && evt.detail && evt.detail.cart;
    if (c && Array.isArray(c.items)) pendingCart = c;
    clearTimeout(timer);
    timer = setTimeout(reconcile, CONFIG.debounceMs);
  }

  document.addEventListener("ajaxProduct:added", schedule);
  document.addEventListener("cart:updated", schedule);
  document.addEventListener("DOMContentLoaded", () => setTimeout(reconcile, 400));
})();